import { useEffect, useMemo, useState } from "react";

import { endOfWeek, getWeekOfMonth, startOfWeek } from "date-fns";
import {
  MailIcon,
  MessageSquareMoreIcon,
  PlusCircle,
  TriangleAlert,
} from "lucide-react";

import { CampaignGroupsDialog } from "src/components/Analytics/MarketingActivity/CampaignGroupsDialog";
import { MarketingEventDialog } from "src/components/Analytics/MarketingActivity/MarketingEventDialog";
import { DataTable } from "src/components/dataView/DataTable/DataTable";
import {
  colorGroupColumn,
  colorGroupLabelColumn,
  headerTooltip,
} from "src/components/dataView/DataTable/tableHelpers";
import { DateRangePicker } from "src/components/DateRangePicker/DateRangePicker";
import { GoogleAdsIcon } from "src/components/icon/GoogleAdsIcon";
import { MetaIcon } from "src/components/icon/MetaIcon";
import DashboardLayout from "src/layouts/DashboardLayout/DashboardLayout";
import { marketingIntegrationTypes } from "src/lib/constants";
import {
  marketingActivityAtom,
  useAnalytics,
} from "src/lib/hooks/useAnalytics";
import { MARKETING_ACTIVITY_QUERY } from "src/lib/queries/marketingActivity";
import { cn } from "src/lib/utils";

export const marketingEventsSubtypes = {
  GOOGLE: {
    label: "Google",
    icon: GoogleAdsIcon,
  },
  META: {
    label: "Meta",
    icon: MetaIcon,
  },
  EMAIL: {
    label: "Email",
    icon: MailIcon,
  },
  SMS: {
    label: "SMS",
    icon: MessageSquareMoreIcon,
  },
};

const loadingData = Array.from({ length: 14 }, (_, i) => ({
  date: new Date(Date.now() - i * 240 * 60 * 60 * 1000)
    .toISOString()
    .split("T")[0],
}));

const columnSubRowsSumFunction = (accessor) => (row) =>
  (row.subRows || row.hiddenSubRows || [row]).reduce(
    (sum, r) => sum + (accessor(r) || 0),
    0
  );

const columnSubRowsAverageFunction = (accessor) => (row) => {
  const subRows = row.subRows || row.hiddenSubRows || [row];
  const total = subRows.reduce((sum, r) => sum + (accessor(r) || 0), 0);
  return total / subRows.length || 0;
};

const MarketingActivity = ({
  atom = marketingActivityAtom,
  marketingIntegrationType,
}) => {
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [selectedEventsData, setSelectedEventsData] = useState({});

  const analytics = useAnalytics(
    atom,
    MARKETING_ACTIVITY_QUERY,
    "marketingActivity",
    ({ data }) => {
      const monthlyRows = [];
      const weeklyRows = [];

      data.forEach((item) => {
        const date = new Date(item.date + "T00:00:00");
        const weekOfMonth = getWeekOfMonth(date, {
          weekStartsOn: 1,
        });

        const monthLabel = date.toLocaleDateString("en-US", {
          month: "short",
          year: "numeric",
        });
        const weekLabel = `${monthLabel} - Week ${weekOfMonth}`;

        let weekly = weeklyRows.find((g) => g.label === weekLabel);
        if (!weekly) {
          weekly = {
            id: weekLabel,
            label: weekLabel,
            meta: { color: "var(--primary)" },
            subRows: [],
            events: [],
            dateFrom: startOfWeek(date, { weekStartsOn: 1 }),
            dateTo: endOfWeek(date, { weekStartsOn: 1 }),
          };
          weeklyRows.push(weekly);
        }

        let monthly = monthlyRows.find((g) => g.label === monthLabel);
        if (!monthly) {
          monthly = {
            id: monthLabel,
            label: monthLabel,
            meta: { colorDot: "var(--primary)" },
            hiddenSubRows: [],
            events: [],
            dateFrom: new Date(date.getFullYear(), date.getMonth(), 1),
            dateTo: new Date(date.getFullYear(), date.getMonth() + 1, 0),
          };
          monthlyRows.push(monthly);
        }

        const subRow = {
          label: date.toLocaleDateString("en-US", {
            weekday: "short",
            month: "short",
            day: "numeric",
            year: "numeric",
          }),
          ...item,
          date,
        };
        weekly.subRows.push(subRow);
        monthly.hiddenSubRows.push(subRow);

        subRow.events.forEach((e) => {
          if (!monthly.events.find(({ id }) => id === e.id)) {
            monthly.events.push(e);
          }
          if (!weekly.events.find(({ id }) => id === e.id)) {
            weekly.events.push(e);
          }
        });
      });

      if (monthlyRows.length > 0) {
        const lastIdx = monthlyRows.length - 1;
        monthlyRows[lastIdx].meta = {
          ...monthlyRows[lastIdx].meta,
          className: "border-b-2 border-b-muted-foreground",
        };
      }

      return [...monthlyRows, ...weeklyRows];
    }
  );
  const { refreshData, store, selectedIntegration, loading, numberFormat } =
    analytics;
  const { data = {} } = store;
  const blendedCpaLabel = marketingIntegrationType ? "CPA" : "Blended CPA";

  const dynamicGroupColumns = useMemo(() => {
    let groupKeys = new Map();

    for (const row of data || []) {
      for (const g of row.subRows?.[0]?.groups || []) {
        const rawKey = g.name;
        if (!rawKey || rawKey.toLowerCase() === "sot") continue;

        if (!groupKeys.has(rawKey)) {
          const integration = marketingIntegrationTypes[rawKey.toLowerCase()];
          const icon =
            rawKey.toLowerCase() === "uncategorized"
              ? TriangleAlert
              : integration?.icon || null;
          groupKeys.set(rawKey, {
            label: integration?.name || rawKey,
            icon,
          });
        }
      }
    }
    groupKeys = Array.from(groupKeys.entries());

    // move "Uncategorized" group to the end, if one exists
    const uncategorizedIndex = groupKeys.findIndex(
      ([, { label }]) => label.toLowerCase() === "uncategorized"
    );
    if (uncategorizedIndex !== -1) {
      const uncategorizedGroup = groupKeys.splice(uncategorizedIndex, 1)[0];
      groupKeys.push(uncategorizedGroup);
    }

    return groupKeys.map(([groupKey, { label, icon: Icon }]) => {
      const findGroup = (row) => row.groups?.find((g) => g.name === groupKey);

      return {
        id: `group-${groupKey}`,
        header: () => (
          <span className="inline-flex items-center">
            {Icon && <Icon className="mr-2 size-4" />}
            {label}
          </span>
        ),
        columns: [
          {
            id: `group-${groupKey}-cost`,
            header: "Cost",
            accessorFn: columnSubRowsSumFunction((row) => findGroup(row)?.cost),
            cell: ({ getValue }) => numberFormat.currency(getValue()),
            meta: {
              leftBorder: true,
              className: "border-l-muted-foreground text-right",
            },
          },
          {
            id: `group-${groupKey}-conversions`,
            header: "Conversions",
            accessorFn: columnSubRowsSumFunction(
              (row) => findGroup(row)?.conversions
            ),
            cell: ({ getValue }) => numberFormat.whole(getValue()),
            meta: { className: "text-right" },
          },
          {
            id: `group-${groupKey}-revenue`,
            header: "Revenue",
            accessorFn: columnSubRowsSumFunction(
              (row) => findGroup(row)?.revenue
            ),
            cell: ({ getValue }) => numberFormat.currency(getValue()),
            meta: { className: "text-right" },
          },
          {
            id: `group-${groupKey}-cpa`,
            header: "CPA",
            accessorFn: columnSubRowsAverageFunction((row) => {
              const g = findGroup(row);
              return g?.cost / (g?.conversions || 1);
            }),
            cell: ({ getValue }) => numberFormat.currency(getValue()),
            meta: { className: "text-right" },
          },
          {
            id: `group-${groupKey}-roas`,
            header: "ROAS",
            accessorFn: columnSubRowsAverageFunction((row) => {
              const g = findGroup(row);
              return g?.revenue / (g?.cost || 1);
            }),
            cell: ({ getValue }) => numberFormat.whole(getValue(), 2),
            meta: { className: "text-right" },
          },
        ],
      };
    });
  }, [data, numberFormat]);

  useEffect(() => {
    if (selectedEventsData && data) {
      // update events when data is updated
      const row = data.find(
        (r) => (r.id || r.label) === selectedEventsData.rowId
      );
      if (row) {
        setSelectedEventsData({
          ...selectedEventsData,
          events: row.events,
        });
      } else {
        setSelectedEventsData({});
        setEventDialogOpen(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const columns = useMemo(() => {
    return [
      colorGroupColumn,
      {
        ...colorGroupLabelColumn,
        meta: {
          ...colorGroupLabelColumn.meta,
          rightBorder: false,
          noSkeleton: false,
        },
      },
      {
        id: "events",
        header: "",
        meta: {
          rightBorder: true,
        },
        cell: ({ row, table }) => {
          if (table.options.meta?.loading) {
            return <div className="w-22" />;
          }

          const hasEvents = row.original.events?.length;
          if (!hasEvents) {
            return (
              <button
                className="group/events text-muted-foreground hover:text-foreground flex w-22 cursor-pointer items-center"
                onClick={(e) => {
                  e.stopPropagation();

                  setSelectedEventsData({
                    rowId: row.original.id || row.id,
                    dateFrom: row.original.dateFrom || row.original.date,
                    dateTo: row.original.dateTo,
                  });
                  setEventDialogOpen(true);
                }}
              >
                <span className="group-hover/events:hidden">No events</span>
                <span className="hidden group-hover/events:inline">
                  Add event
                </span>
                <PlusCircle className="text-primary ml-1.5 hidden size-4 group-hover/events:inline" />
              </button>
            );
          }

          const events = row.original.events.slice(0, 3);
          if (row.original.events.length > 3) {
            events.push({ id: "more" });
          }
          events.push({ id: "add" });

          return (
            <button
              className="group/events flex w-22 cursor-pointer items-center"
              onClick={(e) => {
                e.stopPropagation();

                setSelectedEventsData({
                  rowId: row.original.id || row.id,
                  dateFrom: row.original.dateFrom || row.original.date,
                  dateTo: row.original.dateTo,
                  events: row.original.events,
                });
                setEventDialogOpen(true);
              }}
            >
              <div className="relative flex w-full items-center">
                {events.map((event, i) => {
                  const subtype = event.subtype?.toUpperCase();
                  const Icon = marketingEventsSubtypes[subtype]?.icon;

                  return (
                    <div
                      key={event.id}
                      className={cn(
                        "dark:text-background text-foreground border transition-all rounded-full size-6 p-1 flex items-center justify-center dark:bg-foreground bg-background",
                        i > 0 && "-ml-1 group-hover/events:-ml-2.5",
                        event.id === "add" &&
                          "hidden group-hover/events:inline-flex p-0 dark:bg-primary"
                      )}
                    >
                      {Icon && <Icon className="size-4" />}
                      {event.id === "more" && (
                        <span className="text-xs font-semibold font-mono">
                          +{row.original.events.length - 3}
                        </span>
                      )}
                      {event.id === "add" && (
                        <PlusCircle className="text-primary dark:text-foreground size-4" />
                      )}
                    </div>
                  );
                })}
              </div>
            </button>
          );
        },
      },
      {
        id: "sot",
        header: headerTooltip(
          "Source of Truth",
          "Source of Truth metrics from your store (orders, total sales, etc.)"
        ),
        columns: [
          {
            header: "Orders",
            accessorFn: columnSubRowsSumFunction((row) => row.orders),
            cell: ({ getValue }) => numberFormat.whole(getValue()),
            meta: { className: "text-right" },
          },
          {
            header: "Total Sales",
            accessorFn: columnSubRowsSumFunction((row) => row.totalSales),
            cell: ({ getValue }) => numberFormat.currency(getValue()),
            meta: { className: "text-right" },
          },
          {
            header: blendedCpaLabel,
            accessorFn: columnSubRowsAverageFunction((row) => {
              if (row.orders === 0) return 0;

              return row.groups?.[0]?.cost / row.orders;
            }),
            cell: ({ getValue }) => numberFormat.currency(getValue()),
            meta: { className: "text-right" },
          },
          {
            header: "MER",
            accessorFn: columnSubRowsAverageFunction(
              (row) => row.totalSales / (row.groups?.[0]?.cost || 1)
            ),
            cell: ({ getValue }) => numberFormat.whole(getValue(), 2),
            meta: { className: "text-right" },
          },
          {
            header: "AOV",
            meta: {
              rightBorder: true,
              className: "border-r-muted-foreground text-right",
            },
            accessorFn: columnSubRowsAverageFunction(
              (row) => row.totalSales / (row.orders || 1)
            ),
            cell: ({ getValue }) => numberFormat.currency(getValue()),
          },
        ],
      },
      {
        id: "summaryOfMarketing",
        header: headerTooltip(
          "Summary of Marketing Activity",
          "All marketing integrations combined (or the selected integration)"
        ),
        columns: [
          {
            header: "Cost",
            accessorFn: columnSubRowsSumFunction(
              (row) => row.groups?.[0]?.cost
            ),
            cell: ({ getValue }) => numberFormat.currency(getValue()),
            meta: { className: "text-right" },
          },
          {
            header: "Conversions",
            accessorFn: columnSubRowsSumFunction(
              (row) => row.groups?.[0]?.conversions
            ),
            cell: ({ getValue }) => numberFormat.whole(getValue()),
            meta: { className: "text-right" },
          },
          {
            header: "Revenue",
            accessorFn: columnSubRowsSumFunction(
              (row) => row.groups?.[0]?.revenue
            ),
            cell: ({ getValue }) => numberFormat.currency(getValue()),
            meta: { className: "text-right" },
          },
          {
            id: "adCpa", // avoid duplicate key with "CPA"
            header: blendedCpaLabel,
            accessorFn: columnSubRowsAverageFunction((row) => {
              if (row.groups?.[0]?.conversions === 0) return 0;

              return row.groups?.[0]?.cost / row.groups?.[0]?.conversions;
            }),
            cell: ({ getValue }) => numberFormat.currency(getValue()),
            meta: { className: "text-right" },
          },
          {
            header: "ROAS",
            accessorFn: columnSubRowsAverageFunction(
              (row) => row.groups?.[0]?.revenue / (row.groups?.[0]?.cost || 1)
            ),
            cell: ({ getValue }) => numberFormat.whole(getValue(), 2),
            meta: { className: "text-right" },
          },
        ],
      },
      ...dynamicGroupColumns,
    ];
  }, [blendedCpaLabel, dynamicGroupColumns, numberFormat]);

  return (
    <>
      <DashboardLayout.Header onRefresh={refreshData} loading={store.loading} />
      <DashboardLayout.Main>
        <DashboardLayout.Filters className="justify-between">
          <DateRangePicker atom={atom} onSubmit={refreshData} />

          {marketingIntegrationType && (
            <CampaignGroupsDialog
              atom={atom}
              integrationId={selectedIntegration?.id}
              marketingIntegrationType={marketingIntegrationType}
              onUpdate={refreshData}
            />
          )}
        </DashboardLayout.Filters>

        <DataTable
          idField="date"
          columns={columns}
          data={data}
          loadingData={loadingData}
          atom={atom}
          alternateRowColors
        />

        <MarketingEventDialog
          open={eventDialogOpen}
          setOpen={setEventDialogOpen}
          integrationId={selectedIntegration?.id}
          data={selectedEventsData}
          onUpdate={refreshData}
          loading={loading}
        />
      </DashboardLayout.Main>
    </>
  );
};

export default MarketingActivity;
